import { SingleAssetUsage, BoundingBox, Point } from '@/store/modules/asset'
import { AppModule } from '@/store/modules/app'
import { ErrorPayload, ErrorsModule } from '@/store/modules/error'
import { ToolbarModule } from '@/store/modules/toolbar'
import { AssetsModule } from '@/store/modules/asset'
import i18n from '@/i18n'

export class ImageClipper {

    private _imageSize: [number, number] = [0, 0]
    private _center: [number, number] = [0, 0]

    private _zoom: number
    private _rotation: number
    private _minScale: number = 1.0
    private _maxScale: number = 1.0
    private _error: boolean = false

    private static _umlauf: number = 5
    private static _minPpi: number = 43.271

    private constructor(
            public displaySize: [number, number], 
            public minSize: [number, number],
            public displayOffset: [number, number],
            private targetSize: [number, number] | undefined,
            private _usageName?: string, 
            private changedCenter?: (center: [number,number]) => void,
            private changedError?: (hasError: boolean) => void) {
        this._zoom = 0.0
        this._rotation = 0.0
    }

    public static getImageClipper(active: boolean, stepIdx?: number, usageName?: string): ImageClipper {
        let step = AppModule.steps[stepIdx || AppModule.currentStepIndex] 
        let clipRects = step.clipRects
        let usage = usageName || step.usageName
        if (clipRects) {
            let targetSize: [number, number] | undefined = undefined
            if(clipRects.length > 3 && clipRects[3]) {
                targetSize = [clipRects[3][0], clipRects[3][1]]
            }
            if (active) {
                return new ImageClipper(clipRects[0], clipRects[1], clipRects[2], targetSize,
                    usage,
                    (center) => ToolbarModule.SetCenter(center),
                    (hasError) => {
                        if (usage) {
                            ToolbarModule.SetToolbarError(hasError)
                            let error: ErrorPayload = { key: usage }
                            if (hasError) {
                                let assetName = ''
                                if (AssetsModule.selectedAssets[0] && AssetsModule.selectedAssets[0].name) {
                                    assetName = AssetsModule.selectedAssets[0].name
                                }
                                error.message = i18n.t('selectedImage').toString() + assetName + i18n.t('tooSmall').toString()
                                    + i18n.t('pleaseChooseAnother').toString() + clipRects![1][0] + 'x' + clipRects![1][1] + i18n.t('pxSize').toString()
                            }
                            ErrorsModule.SetError(error)
                        }
                    })
            } else {
                return new ImageClipper(clipRects[0], clipRects[1], clipRects[2], targetSize)
            }
        }
        throw new Error('no image clipper defined for current step: ' + AppModule.currentStepIndex)
    }

    public initializeUsage(usage: SingleAssetUsage, size: [number, number]) {
        this._error = false
        this._imageSize = size
        this._center = [size[0] / 2.0, size[1] / 2.0]
        this._rotation = 0
        this.rotate(usage.rotation)
        this._zoom = usage.zoom
        this.adjustScaleRange()
        if(usage.center[0] > 0 && usage.center[1] > 0) {
            this._center = [...usage.center] as [number, number]
        }
        this.adjustCenter()
    }

    public reset() {
        this._error = false
    }

    get imageSize(): [number, number] {
        return this._imageSize
    }

    get scale(): number {
        return this._minScale + (this._maxScale - this._minScale) * (this._zoom / 100.0)
    }

    get rotation(): number {
        return this._rotation
    }

    get translateX(): number {
        let scale = this.scale
        let width = this._imageSize[0]  
        let translateX = (this.displaySize[0] / (2.0 * scale)) - this._center[0]
        switch(this._rotation) {
            case 90:
            case 180:
                return translateX + width
        }
        return translateX
    }

    get translateY(): number {
        let scale = this.scale
        let height = this._imageSize[1]  
        let translateY = (this.displaySize[1] / (2.0 * scale)) - this._center[1]
        switch(this._rotation) {
            case 270:
            case 180:
                return translateY + height
        }
        return translateY
    }

    // origin is upper left
    get boundingBox(): BoundingBox {
        let scale = this.scale
        let centerDiffX = this.displaySize[0] / (2.0 * scale)
        let centerDiffY = this.displaySize[1] / (2.0 * scale)
        let llx = this._center[0] - centerDiffX 
        let urx = this._center[0] + centerDiffX
        let lly = this._center[1] - centerDiffY
        let ury = this._center[1] + centerDiffY
        let ll: Point = [llx, lly]
        let ur: Point = [urx, ury]
        return { ll, ur }
    }

    set zoom(zoom: number) {
        this._zoom = zoom
        this.adjustCenter()
    }
    
    get zoom() {
        return this._zoom
    }

    set rotation(rotation: number) {
        this.rotate(rotation)
        this.adjustScaleRange()
        this.adjustCenter()
        if (!this.adjustCenter() && this.changedCenter) {
            this.changedCenter([this._center[0], this._center[1]])
        }
    }

    move(offset: [number, number]) {
        let scale = this.scale
        this.adjustCenter([this._center[0] - (offset[0] / scale),this._center[1] - (offset[1] / scale)])
    }

    get transformation(): string {
        return 'transform: scale(' 
                    + this.scale + ') translateX('
                    + this.translateX + 'px) translateY('
                    + this.translateY + 'px) rotate(' 
                    + this.rotation + 'deg)'
    }

    get usageName(): string | undefined {
        return this._usageName
    }

    private rotate(rotation: number) {
        let rotationDiff = rotation - this._rotation
        if (rotationDiff < 0) {
            rotationDiff += 360
        }
        switch (rotationDiff) {
            case 90:
                this._imageSize = [this._imageSize[1], this._imageSize[0]]
                this._center = [this._imageSize[0] - this._center[1], this._center[0]]
                break
            case 180:
                this._center = [this._imageSize[0] - this._center[0], this._imageSize[1] - this._center[1]]
                break
            case 270:
                this._imageSize = [this._imageSize[1], this._imageSize[0]]
                this._center = [this._center[1], this._imageSize[1] - this._center[0]]
                break
        }
        this._rotation = rotation
    }

    private adjustCenter(newCenter?: [number, number]): boolean {
        let border = this.border
        let scale = this.scale
        let minx = (this.displaySize[0] / (2.0 * scale)) + border
        let miny = (this.displaySize[1] / (2.0 * scale)) + border
        let changed = newCenter !== undefined
        let cx = newCenter ? newCenter[0] : this._center[0]
        let cy = newCenter ? newCenter[1] : this._center[1]
        if (cx < minx) {
            cx = minx
            changed = true
        } else if (cx > this._imageSize[0] - minx) {
            cx = this._imageSize[0] - minx
            changed = true
        }
        if (cy < miny) {
            cy = miny
            changed = true
        } else if (cy > this._imageSize[1] - miny) {
            cy = this._imageSize[1] - miny
            changed = true
        }
        if (changed) {
            this._center = [cx, cy]
            if (this.changedCenter) {
                this.changedCenter([cx, cy])
                return true
            }
        }
        return false
    }

    private adjustScaleRange() {
        let border = this.border
        let minScaleX = this.displaySize[0] / (this._imageSize[0] - border * 2)
        let minScaleY = this.displaySize[1] / (this._imageSize[1] - border * 2)
        let maxScaleX = this.displaySize[0] / this.minSize[0]
        let maxScaleY = this.displaySize[1] / this.minSize[1]
        this._minScale = Math.max(minScaleX, minScaleY)
        this._maxScale = this.maxZoom()

        if (minScaleX > maxScaleX || minScaleY > maxScaleY) {
            this._maxScale = this._minScale
            if (!this._error) {
                this._error = true
                if (this.changedError) {
                    this.changedError(this._error)
                }
            }
        } else {
            if (this._error) {
                this._error = false
                if (this.changedError) {
                    this.changedError(this._error)
                }
            }
        }
    }

    get border(): number {
        if(this.targetSize) {
            var border
            if (this.targetSize[1] > this.targetSize[0]) {
                border = Math.round(this._imageSize[1] * ImageClipper._umlauf / this.targetSize[1])
            } else {
                border = Math.round(this._imageSize[0] * ImageClipper._umlauf / this.targetSize[0])
            }
            return border
        }
        return 0
    }

    private maxZoom(): number {
        if(this.targetSize) {
            var targetWidth = this.targetSize[0]
            var targetHeight = this.targetSize[1]
            targetWidth = targetWidth - ImageClipper._umlauf
            targetHeight = targetHeight - ImageClipper._umlauf
            var targetLongSide = Math.max(targetWidth, targetHeight)
            var minActualLongSide = (targetLongSide / 25.4) * ImageClipper._minPpi
            return Math.max(this.displaySize[0] ,this.displaySize[1]) / minActualLongSide
        }
        return Math.min(this.displaySize[0] / this.minSize[0], this.displaySize[1] / this.minSize[1])
    }

}